import React, { useContext, useEffect, useState } from 'react';
import { Circle, Document, Font, Image, Page, Path, PDFViewer, StyleSheet, Svg, Text, View } from '@react-pdf/renderer';
import axios from 'axios';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { toBnNumber } from '../../../../../common/functions';
import kalpurushFont from '../../../../../assets/font/Kalpurush.ttf';
import childLogo from '../../../../../assets/image/logo/child.png';
import dghsLogo from '../../../../../assets/image/logo/dghs.png';
import dsheLogo from '../../../../../assets/image/logo/dshe.png';
import logo from '../../../../../assets/image/logo/logo.png';
import unicefLogo from '../../../../../assets/image/logo/unicef.png';
import BreadCrumb from '../../../../../Components/BreadCrumb/BreadCrumb';
import LoadingSpinner from '../../../../../Components/LoadingSpinner';
import LanguageContext from '../../../../../context/LanguageContext';

Font.register({
  family: 'Kalpurush',
  src: kalpurushFont,
});

Font.registerHyphenationCallback((word) => [word]);

const COLORS = {
  primary: '#1B6CB3',
  primaryDark: '#155A94',
  page: '#DDE5EC',
  sheet: '#F4F7FA',
  card: '#FFFFFF',
  border: '#D7E2EA',
  text: '#2C3E50',
  muted: '#6B7C8A',
  headBar: '#E7F2FB',
  footer: '#EEF3F7',
};

const BMI_SCALE_BANDS = [
  {
    key: 'severe',
    labelId: 'bmiSevereMalnourished',
    getRange: (localize) => `( < ${localize('14.5')} )`,
    color: '#F5D0CE',
    textColor: '#E74C3C',
    mouth: 'sad',
    sweep: 72,
  },
  {
    key: 'moderate',
    labelId: 'bmiModeratelyMalnourished',
    getRange: (localize) => `(${localize('14.5')} - ${localize('16.9')})`,
    color: '#FAD7A0',
    textColor: '#E67E22',
    mouth: 'slightFrown',
    sweep: 144,
  },
  {
    key: 'normal',
    labelId: 'bmiNormal',
    getRange: (localize) => `(${localize('17.0')} - ${localize('22.9')})`,
    color: '#D5F5E3',
    textColor: '#27AE60',
    mouth: 'smile',
    sweep: 216,
  },
  {
    key: 'overweight',
    labelId: 'bmiOverweight',
    getRange: (localize) => `(${localize('23.0')} - ${localize('24.9')})`,
    color: '#FCF3CF',
    textColor: '#F39C12',
    mouth: 'worried',
    sweep: 288,
  },
  {
    key: 'obese',
    labelId: 'bmiObese',
    getRange: (localize) => `( >= ${localize('25.0')} )`,
    color: '#F5B7B1',
    textColor: '#C0392B',
    mouth: 'sad',
    sweep: 359,
  },
];

const UNKNOWN_STATUS = {
  key: 'unknown',
  color: '#D5DBDB',
  textColor: '#5D6D7E',
  mouth: 'straight',
  sweep: 40,
};

const getScaleBands = (localize) =>
  BMI_SCALE_BANDS.map((band) => ({
    ...band,
    range: band.getRange(localize),
  }));

const getStyles = (font, fontBold, isBn) =>
  StyleSheet.create({
    viewer: {
      width: '100%',
      height: '100%',
      minHeight: 700,
      margin: 0,
      padding: 0,
    },
    page: {
      backgroundColor: COLORS.page,
      color: COLORS.text,
      padding: 0,
      margin: 0,
      fontFamily: font,
    },
    sheet: {
      backgroundColor: COLORS.sheet,
      overflow: 'hidden',
      minHeight: '100%',
      height: '100%',
      flexDirection: 'column',
    },
    header: {
      backgroundColor: COLORS.primary,
      paddingHorizontal: 16,
      paddingVertical: 10,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    headerLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      flexGrow: 1,
    },
    logoBadge: {
      width: 42,
      height: 42,
      borderRadius: 21,
      backgroundColor: '#FFFFFF',
      padding: 3,
      marginRight: 10,
    },
    logoImage: {
      width: 36,
      height: 36,
      borderRadius: 18,
    },
    headerTitle: {
      color: '#FFFFFF',
      fontSize: 15,
      fontFamily: fontBold,
      letterSpacing: 0.2,
    },
    headerSubtitle: {
      color: '#D7E8F7',
      fontSize: 8,
      marginTop: 2,
    },
    headerRight: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    headerChildLogo: {
      width: 110,
      height: 42,
      objectFit: 'contain',
    },
    body: {
      flexDirection: 'row',
      alignItems: 'stretch',
      paddingTop: 4,
      paddingBottom: 4,
      paddingHorizontal: 0,
      flexGrow: 1,
    },
    leftCard: {
      width: '38%',
      backgroundColor: COLORS.card,
      borderWidth: 1,
      borderColor: COLORS.border,
      borderRadius: 10,
      overflow: 'hidden',
      marginRight: 5,
    },
    rightCol: {
      width: '62%',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    card: {
      backgroundColor: COLORS.card,
      borderWidth: 1,
      borderColor: COLORS.border,
      borderRadius: 10,
      overflow: 'hidden',
    },
    metricsCard: {
      backgroundColor: COLORS.card,
      borderRadius: 10,
      overflow: 'hidden',
    },
    sectionHead: {
      backgroundColor: COLORS.headBar,
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderBottomWidth: 2,
      borderBottomColor: COLORS.primary,
    },
    sectionTitle: {
      fontSize: 9.5,
      fontFamily: fontBold,
      color: COLORS.primary,
      letterSpacing: 0.8,
      marginLeft: 6,
    },
    cardBody: {
      paddingHorizontal: 10,
      paddingVertical: 3,
    },
    infoRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 3.2,
      borderBottomWidth: 0.7,
      borderBottomColor: '#EDF2F6',
    },
    infoRowLast: {
      borderBottomWidth: 0,
    },
    iconWrap: {
      marginRight: 6,
    },
    infoLabel: {
      width: isBn ? 98 : 84,
      fontSize: 7.5,
      color: COLORS.muted,
    },
    infoColon: {
      width: 6,
      fontSize: 7.5,
      color: COLORS.muted,
    },
    infoValue: {
      flex: 1,
      fontSize: 7.5,
      color: COLORS.text,
    },
    resultCard: {
      marginBottom: 6,
      flexGrow: 1,
    },
    resultBody: {
      flexGrow: 1,
      justifyContent: 'space-evenly',
      paddingHorizontal: 12,
      paddingVertical: 6,
    },
    resultTop: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: 6,
      paddingBottom: 8,
    },
    gaugeWrap: {
      marginRight: 16,
      alignItems: 'center',
      justifyContent: 'center',
    },
    resultStats: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    statBlock: {
      flex: 1,
      paddingHorizontal: 12,
      alignItems: 'center',
    },
    statLabel: {
      fontSize: 8,
      fontFamily: fontBold,
      color: COLORS.primary,
      letterSpacing: 0.6,
      marginBottom: 4,
      textAlign: 'center',
    },
    bmiNumber: {
      fontSize: 24,
      fontFamily: fontBold,
      color: COLORS.primary,
      textAlign: 'center',
    },
    categoryValue: {
      fontSize: 11,
      fontFamily: fontBold,
      lineHeight: 1.3,
      textAlign: 'center',
    },
    vDivider: {
      width: 1,
      height: 48,
      backgroundColor: '#D5DEE6',
    },
    scaleWrap: {
      paddingTop: 4,
      paddingBottom: 4,
    },
    pointerRow: {
      flexDirection: 'row',
      height: 10,
      marginBottom: 2,
    },
    pointerSlot: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingHorizontal: 2,
    },
    scaleBar: {
      flexDirection: 'row',
      alignItems: 'stretch',
    },
    scaleCol: {
      flex: 1,
      paddingHorizontal: 2,
    },
    scaleBox: {
      flexGrow: 1,
      minHeight: 48,
      borderRadius: 3,
      borderWidth: 0.8,
      borderColor: '#2C3E50',
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 6,
      paddingHorizontal: 4,
      paddingRight: 6,
    },
    scaleBoxActive: {
      borderWidth: 1.6,
    },
    scaleLabel: {
      fontSize: 7,
      fontFamily: fontBold,
      textAlign: 'center',
      lineHeight: 1.2,
      marginBottom: 3,
    },
    scaleRange: {
      fontSize: 7,
      fontFamily: fontBold,
      color: COLORS.primary,
      textAlign: 'center',
    },
    metrics: {
      flexDirection: 'row',
      alignItems: 'stretch',
      minHeight: 88,
      paddingVertical: 10,
      paddingHorizontal: 6,
    },
    metricCol: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'flex-start',
      paddingHorizontal: 4,
      paddingVertical: 2,
    },
    metricIconWrap: {
      width: 22,
      height: 22,
      alignItems: 'center',
      justifyContent: 'center',
    },
    metricDivider: {
      width: 1,
      backgroundColor: '#DDE5EC',
      alignSelf: 'stretch',
      marginVertical: 6,
    },
    metricLabel: {
      height: 12,
      fontSize: 7.5,
      color: COLORS.muted,
      letterSpacing: 0.8,
      marginTop: 6,
      marginBottom: 3,
      textTransform: 'capitalize',
      textAlign: 'center',
    },
    metricValueWrap: {
      minHeight: 24,
      width: '100%',
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
    metricValue: {
      fontSize: 9.5,
      fontFamily: fontBold,
      color: COLORS.primaryDark,
      textAlign: 'center',
      lineHeight: 1.25,
    },
    footer: {
      backgroundColor: COLORS.footer,
      paddingHorizontal: 14,
      paddingVertical: 8,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderTopWidth: 1,
      borderTopColor: COLORS.border,
    },
    footerLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      flexGrow: 1,
      paddingRight: 10,
    },
    footerCopy: {
      marginLeft: 8,
      flexGrow: 1,
      paddingRight: 8,
    },
    footerText: {
      fontSize: 8,
      color: COLORS.muted,
      marginBottom: 2,
    },
    footerStrong: {
      fontSize: 8,
      fontFamily: fontBold,
      color: COLORS.primaryDark,
    },
    footerLogos: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    footerLogoItem: {
      height: 32,
      marginRight: 12,
      alignItems: 'center',
      justifyContent: 'center',
    },
    footerLogoItemLast: {
      height: 32,
      alignItems: 'center',
      justifyContent: 'center',
    },
    footerPartnerLogo: {
      width: 32,
      height: 32,
      objectFit: 'contain',
    },
    footerUnicefLogo: {
      width: 62,
      height: 28,
      objectFit: 'contain',
    },
  });

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const localizeValue = (value, isBn) => {
  if (value === null || value === undefined || value === '') return '-';
  return isBn ? toBnNumber(value) : String(value);
};

const translateBmiStatus = (status, intl) => {
  const value = (status || '').toLowerCase();
  if (value.includes('severe')) return intl.formatMessage({ id: 'bmiSevereMalnourished' });
  if (value.includes('moderately')) return intl.formatMessage({ id: 'bmiModeratelyMalnourished' });
  if (value.includes('mild mal')) return intl.formatMessage({ id: 'bmiMildMalnourished' });
  if (value.includes('obesity') || value.includes('obese')) return intl.formatMessage({ id: 'bmiObesity' });
  if (value.includes('mild over')) return intl.formatMessage({ id: 'bmiMildOverweight' });
  if (value.includes('overweight')) return intl.formatMessage({ id: 'bmiOverweight' });
  if (value.includes('normal')) return intl.formatMessage({ id: 'bmiNormal' });
  return formatValue(status);
};

const translateGender = (gender, intl) => {
  if (gender === 'Boy' || gender === 'Girl') {
    return intl.formatMessage({ id: gender });
  }
  return formatValue(gender);
};

const getBandByKey = (key) => BMI_SCALE_BANDS.find((band) => band.key === key) || UNKNOWN_STATUS;

const getStatusMeta = (status) => {
  const value = (status || '').toLowerCase();
  if (value.includes('severe')) return getBandByKey('severe');
  if (value.includes('moderately') || value.includes('mild mal')) return getBandByKey('moderate');
  if (value.includes('obesity') || value.includes('obese')) return getBandByKey('obese');
  if (value.includes('overweight')) return getBandByKey('overweight');
  if (value.includes('normal')) return getBandByKey('normal');
  return UNKNOWN_STATUS;
};

const polarToCartesian = (cx, cy, r, angleInDegrees) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(angleInRadians),
    y: cy + r * Math.sin(angleInRadians),
  };
};

const describeArc = (cx, cy, r, startAngle, endAngle) => {
  const start = polarToCartesian(cx, cy, r, startAngle);
  const end = polarToCartesian(cx, cy, r, endAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${r} ${r} 0 ${largeArcFlag} 1 ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
};

const gaugeSweepFromBmi = (bmiCount, fallbackSweep) => {
  const value = Number(bmiCount);
  if (Number.isNaN(value)) return fallbackSweep;
  const min = 12;
  const max = 28;
  const pct = Math.min(1, Math.max(0.08, (value - min) / (max - min)));
  return pct * 359;
};

const mouthPathFor = (mouth) => {
  if (mouth === 'smile') return 'M 34 56 Q 50 76 66 56';
  if (mouth === 'slightFrown') return 'M 38 63 Q 50 68 62 63';
  if (mouth === 'worried') return 'M 36 66 Q 50 55 64 66';
  if (mouth === 'sad' || mouth === 'frown') return 'M 32 64 Q 50 46 68 64';
  return 'M 36 62 L 64 62';
};

const eyebrowPathsFor = (mouth) => {
  if (mouth === 'sad' || mouth === 'frown') {
    return ['M 32 39 L 44 44', 'M 68 39 L 56 44'];
  }
  if (mouth === 'worried') {
    return ['M 32 42 L 44 37', 'M 68 42 L 56 37'];
  }
  if (mouth === 'smile') {
    return ['M 32 41 Q 40 37 46 41', 'M 68 41 Q 60 37 54 41'];
  }
  return ['M 33 40 L 45 40', 'M 67 40 L 55 40'];
};

const BmiStatusGauge = ({ color, fillColor = '#FFFFFF', sweep = 144, mouth = 'straight', size = 92 }) => {
  const cx = 50;
  const cy = 50;
  const radius = 36;
  const arcSweep = Math.min(359, Math.max(8, sweep));

  return (
    <Svg width={size} height={size} viewBox='0 0 100 100'>
      <Circle cx={cx} cy={cy} r={radius} stroke={fillColor} strokeWidth='10' fill='none' />
      <Path d={describeArc(cx, cy, radius, 0, arcSweep)} stroke={color} strokeWidth='10' fill='none' strokeLinecap='round' />
      <Circle cx={cx} cy={cy} r='27' fill={fillColor} />
      <Circle cx={cx} cy={cy} r='27' stroke={color} strokeWidth='1.6' fill='none' />
      {eyebrowPathsFor(mouth).map((d) => (
        <Path key={d} d={d} stroke={color} strokeWidth='2.6' fill='none' strokeLinecap='round' />
      ))}
      <Circle cx='40' cy='47' r='3.4' fill={color} />
      <Circle cx='60' cy='47' r='3.4' fill={color} />
      <Path d={mouthPathFor(mouth)} stroke={color} strokeWidth='3.2' fill='none' strokeLinecap='round' />
    </Svg>
  );
};

const OutlineIcon = ({ children, size = 13 }) => (
  <Svg width={size} height={size} viewBox='0 0 24 24'>
    {children}
  </Svg>
);

const stroke = {
  fill: 'none',
  stroke: COLORS.primary,
  strokeWidth: 1.8,
};

const FieldIcon = ({ name, size = 13 }) => {
  const icons = {
    building: <Path d='M4 21V9l8-5 8 5v12M9 21v-7h6v7M4 21h16' {...stroke} />,
    pin: <Path d='M12 21s7-6.4 7-11a7 7 0 1 0-14 0c0 4.6 7 11 7 11z M12 11.2a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4z' {...stroke} />,
    users: <Path d='M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2 M9.5 7.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z M20 21v-2a3.5 3.5 0 0 0-2.6-3.4 M16.4 3.2a3.5 3.5 0 0 1 0 6.6' {...stroke} />,
    user: <Path d='M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2 M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z' {...stroke} />,
    calendar: <Path d='M7 3.5v3M17 3.5v3M5 8h14M6.5 5.5h11A1.5 1.5 0 0 1 19 7v13a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 20V7A1.5 1.5 0 0 1 6.5 5.5z' {...stroke} />,
    ruler: (
      <React.Fragment>
        <Path d='M9 3.2h6v17.6H9V3.2z' {...stroke} />
        <Path d='M9 7h3.6M9 10.4h2.6M9 13.8h3.6M9 17.2h2.6' {...stroke} />
      </React.Fragment>
    ),
    weight: (
      <React.Fragment>
        <Path d='M6.5 10.2h11v9.3h-11V10.2z' {...stroke} />
        <Path d='M9.2 10.2a2.8 2.8 0 0 1 5.6 0' {...stroke} />
        <Path d='M12 13.2v3.2' {...stroke} />
      </React.Fragment>
    ),
    chart: <Path d='M4 19V5M8 19v-7M12 19V8M16 19v-5M20 19V7' {...stroke} />,
    heart: <Path d='M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 10c0 5.6-7 10-7 10z' {...stroke} />,
    calculator: <Path d='M6.5 3.5h11v17h-11v-17z M8.5 7.2h7M8.5 11.2h2M12 11.2h2M15.5 11.2h2M8.5 15.2h2M12 15.2h2M15.5 15.2h2' {...stroke} />,
  };

  return (
    <View style={{ width: size, height: size, marginRight: size > 14 ? 0 : 7, alignItems: 'center', justifyContent: 'center' }}>
      <OutlineIcon size={size}>{icons[name] || icons.user}</OutlineIcon>
    </View>
  );
};

const PartnerLogos = ({ styles }) => (
  <View style={styles.footerLogos}>
    <View style={styles.footerLogoItem}>
      <Image src={dsheLogo} style={styles.footerPartnerLogo} />
    </View>
    <View style={styles.footerLogoItem}>
      <Image src={dghsLogo} style={styles.footerPartnerLogo} />
    </View>
    <View style={styles.footerLogoItemLast}>
      <Image src={unicefLogo} style={styles.footerUnicefLogo} />
    </View>
  </View>
);

const ShieldIcon = ({ size = 18 }) => (
  <Svg width={size} height={size} viewBox='0 0 22 22'>
    <Path d='M11 2.1l7.3 2.7v5.3c0 4.2-3 7.1-7.3 8.3C6.7 17.2 3.7 14.3 3.7 10.1V4.8L11 2.1z' fill={COLORS.primary} />
    <Path d='M7.3 11.1l2.2 2.2 5.2-5.3' stroke='#FFFFFF' strokeWidth='1.6' fill='none' />
  </Svg>
);

const InfoRow = ({ icon, label, value, color, last, fontBold, styles }) => (
  <View style={[styles.infoRow, last ? styles.infoRowLast : {}]}>
    <FieldIcon name={icon} />
    <Text style={styles.infoLabel}>{label}</Text>
    <Text style={styles.infoColon}>:</Text>
    <Text style={[styles.infoValue, color ? { color, fontFamily: fontBold } : {}]}>{formatValue(value)}</Text>
  </View>
);

const BmiHealthCard = ({ data, isBn, intl }) => {
  const font = isBn ? 'Kalpurush' : 'Helvetica';
  const fontBold = isBn ? 'Kalpurush' : 'Helvetica-Bold';
  const styles = getStyles(font, fontBold, isBn);
  const t = (id) => intl.formatMessage({ id });
  const statusMeta = getStatusMeta(data?.bmi_status);
  const statusText = translateBmiStatus(data?.bmi_status, intl);
  const heightImperial = [
    data?.feet != null ? `${localizeValue(data.feet, isBn)} ${t('ftUnit')}` : '',
    data?.inch != null ? `${localizeValue(data.inch, isBn)} ${t('inUnit')}` : '',
  ]
    .filter(Boolean)
    .join(' ');
  const heightMetric = data?.height_in_meter != null ? `${localizeValue(data.height_in_meter, isBn)} ${t('meterUnit')}` : '';
  const heightText =
    heightImperial && heightMetric
      ? `${heightImperial} (${heightMetric})`
      : heightImperial || heightMetric || '-';
  const weightText = data?.weight != null ? `${localizeValue(data.weight, isBn)} ${t('kgUnit')}` : '-';
  const bmiText = data?.bmi_count != null && data?.bmi_count !== '' ? localizeValue(Number(data.bmi_count).toFixed(2), isBn) : '-';
  const infoRows = [
    { icon: 'building', label: t('institutionName'), value: data?.institution_name },
    { icon: 'pin', label: t('zone'), value: data?.zone_name },
    { icon: 'pin', label: t('district'), value: data?.district_name },
    { icon: 'pin', label: t('upazila'), value: data?.upazila_name },
    { icon: 'users', label: t('class'), value: localizeValue(data?.grade, isBn) },
    { icon: 'users', label: t('shift'), value: data?.shift_name },
    { icon: 'users', label: t('section'), value: data?.section_name },
    { icon: 'user', label: t('rollNumber'), value: localizeValue(data?.roll, isBn) },
    { icon: 'user', label: t('studentName'), value: data?.student_name },
    { icon: 'user', label: t('gender'), value: translateGender(data?.gender, intl) },
    { icon: 'calendar', label: t('dateOfBirth'), value: localizeValue(data?.dob, isBn) },
    { icon: 'ruler', label: t('age'), value: localizeValue(data?.age, isBn) },
    { icon: 'ruler', label: t('height'), value: heightText },
    { icon: 'weight', label: t('weight'), value: weightText },
    { icon: 'chart', label: t('bmi'), value: bmiText },
    { icon: 'chart', label: t('bmiType'), value: statusText, color: statusMeta.textColor },
    { icon: 'calendar', label: t('round'), value: localizeValue(data?.participation_round, isBn) },
    { icon: 'calendar', label: t('date'), value: localizeValue(data?.participation_date, isBn) },
    { icon: 'calendar', label: t('academicYear'), value: localizeValue(data?.academic_year, isBn) },
  ];
  const scaleBands = getScaleBands((value) => localizeValue(value, isBn));

  return (
    <Document>
      <Page size='A4' orientation='landscape' style={styles.page}>
        <View style={styles.sheet} wrap={false}>
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <View style={styles.logoBadge}>
                <Image src={logo} style={styles.logoImage} />
              </View>
              <View>
                <Text style={styles.headerTitle}>{t('bmiHealthCard')}</Text>
                <Text style={styles.headerSubtitle}>{t('bmiHealthCardSubtitle')}</Text>
              </View>
            </View>
            <View style={styles.headerRight}>
              <Image src={childLogo} style={styles.headerChildLogo} />
            </View>
          </View>

          <View style={styles.body}>
            <View style={styles.leftCard}>
              <View style={styles.sectionHead}>
                <FieldIcon name='user' />
                <Text style={styles.sectionTitle}>{t('studentInformation')}</Text>
              </View>
              <View style={styles.cardBody}>
                {infoRows.map((row, index) => (
                  <InfoRow
                    key={row.label}
                    icon={row.icon}
                    label={row.label}
                    value={row.value}
                    color={row.color}
                    last={index === infoRows.length - 1}
                    fontBold={fontBold}
                    styles={styles}
                  />
                ))}
              </View>
            </View>

            <View style={styles.rightCol}>
              <View style={[styles.card, styles.resultCard]}>
                <View style={styles.sectionHead}>
                  <FieldIcon name='chart' />
                  <Text style={styles.sectionTitle}>{t('bmiResult')}</Text>
                </View>
                <View style={styles.resultBody}>
                  <View style={styles.resultTop}>
                    <View style={styles.gaugeWrap}>
                      <BmiStatusGauge
                        color={statusMeta.textColor}
                        fillColor={statusMeta.color}
                        sweep={gaugeSweepFromBmi(data?.bmi_count, statusMeta.sweep)}
                        mouth={statusMeta.mouth}
                      />
                    </View>
                    <View style={styles.resultStats}>
                      <View style={styles.statBlock}>
                        <Text style={styles.statLabel}>{t('yourBmi')}</Text>
                        <Text style={styles.bmiNumber}>{bmiText}</Text>
                      </View>
                      <View style={styles.vDivider} />
                      <View style={styles.statBlock}>
                        <Text style={styles.statLabel}>{t('yourCategory')}</Text>
                        <Text style={[styles.categoryValue, { color: statusMeta.textColor }]}>
                          {statusText}
                        </Text>
                      </View>
                    </View>
                  </View>

                  <View style={styles.scaleWrap}>
                    <View style={styles.pointerRow}>
                      {scaleBands.map((band) => (
                        <View key={`pointer-${band.key}`} style={styles.pointerSlot}>
                          {statusMeta.key === band.key ? (
                            <Svg width='12' height='8' viewBox='0 0 12 8'>
                              <Path d='M6 8 L0 0 L12 0 Z' fill={statusMeta.textColor} />
                            </Svg>
                          ) : null}
                        </View>
                      ))}
                    </View>
                    <View style={styles.scaleBar}>
                      {scaleBands.map((band) => {
                        const active = statusMeta.key === band.key;
                        const label = (
                          <React.Fragment>
                            <Text style={[styles.scaleLabel, { color: band.textColor }]}>
                              {`${t(band.labelId)}\u00A0`}
                            </Text>
                            <Text wrap={false} style={styles.scaleRange}>
                              {`${band.range}\u00A0`}
                            </Text>
                          </React.Fragment>
                        );
                        return (
                          <View key={band.key} style={styles.scaleCol}>
                            <View
                              style={[
                                styles.scaleBox,
                                { backgroundColor: band.color },
                                active ? styles.scaleBoxActive : {},
                                active ? { borderColor: statusMeta.textColor } : {},
                              ]}
                            >
                              {label}
                            </View>
                          </View>
                        );
                      })}
                    </View>
                  </View>
                </View>
              </View>

              <View style={styles.metricsCard}>
                <View style={styles.metrics}>
                  <View style={styles.metricCol}>
                    <View style={styles.metricIconWrap}>
                      <FieldIcon name='ruler' size={20} />
                    </View>
                    <Text style={styles.metricLabel}>{t('height')}</Text>
                    <View style={styles.metricValueWrap}>
                      <Text style={styles.metricValue}>{heightText}</Text>
                    </View>
                  </View>
                  <View style={styles.metricDivider} />
                  <View style={styles.metricCol}>
                    <View style={styles.metricIconWrap}>
                      <FieldIcon name='weight' size={20} />
                    </View>
                    <Text style={styles.metricLabel}>{t('weight')}</Text>
                    <View style={styles.metricValueWrap}>
                      <Text style={styles.metricValue}>{weightText}</Text>
                    </View>
                  </View>
                  <View style={styles.metricDivider} />
                  <View style={styles.metricCol}>
                    <View style={styles.metricIconWrap}>
                      <FieldIcon name='calculator' size={20} />
                    </View>
                    <Text style={styles.metricLabel}>{t('bmi')}</Text>
                    <View style={styles.metricValueWrap}>
                      <Text style={styles.metricValue}>{bmiText}</Text>
                    </View>
                  </View>
                  <View style={styles.metricDivider} />
                  <View style={styles.metricCol}>
                    <View style={styles.metricIconWrap}>
                      <FieldIcon name='calendar' size={20} />
                    </View>
                    <Text style={styles.metricLabel}>{t('date')}</Text>
                    <View style={styles.metricValueWrap}>
                      <Text style={styles.metricValue}>{localizeValue(data?.participation_date, isBn)}</Text>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>

          <View style={styles.footer}>
            <View style={styles.footerLeft}>
              <ShieldIcon />
              <View style={styles.footerCopy}>
                <Text style={styles.footerText}>{t('bmiFooterQuote')}</Text>
                <Text style={styles.footerStrong}>{t('eatHealthyStayActive')}</Text>
              </View>
            </View>
            <PartnerLogos styles={styles} />
          </View>
        </View>
      </Page>
    </Document>
  );
};

const BmiPdfDocument = () => {
  const location = useLocation();
  const intl = useIntl();
  const { defaultLanguage } = useContext(LanguageContext);
  const isBn = defaultLanguage === 'bn';
  const viewerStyles = getStyles('Helvetica', 'Helvetica-Bold', false);
  const bmiId = location.state?.id;
  const [bmiData, setBmiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!bmiId) {
      setLoading(false);
      setError('BMI record not found.');
      return;
    }

    const fetchDetails = async () => {
      setLoading(true);
      try {
        const token = JSON.parse(localStorage.token);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}bmi/participated/student-details-by-id?id=${bmiId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setBmiData(response.data?.data || null);
        setError('');
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load BMI details.');
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [bmiId]);

  return (
    <React.Fragment>
      <BreadCrumb />
      {loading && <LoadingSpinner />}
      {!loading && error && (
        <section className='mx-auto bg-white py-4 lg:py-10'>
          <p className='text-center text-red-600'>{error}</p>
        </section>
      )}
      {!loading && bmiData && (
        <section className='mx-auto bg-white py-4 lg:py-10 space-y-4'>
          <PDFViewer style={viewerStyles.viewer}>
            <BmiHealthCard data={bmiData} isBn={isBn} intl={intl} />
          </PDFViewer>
        </section>
      )}
    </React.Fragment>
  );
};

export default BmiPdfDocument;
